'use client';

import type { FoodItem } from '../types';
import { Leaf, Flame, AlertCircle } from '@/app/Components/icons';
import Modal from '@/app/Components/Modal';
import { OperationalEvidence, OperationalStatus } from '@/app/Components/OperationalPage';
import type { DiningItemImageRecipe } from '../assets';
import DiningItemPhoto from './DiningItemPhoto';

function NutritionRow({ label, value, unit }: { label: string; value: number | null; unit: string }) {
  if (value === null || value === undefined) return null;
  return <div className="flex justify-between gap-4 border-b border-[var(--ss-border)] py-2.5 text-sm last:border-0"><span className="text-[var(--ss-text-muted)]">{label}</span><span className="font-semibold tabular-nums text-[var(--ss-text)]">{value}{unit}</span></div>;
}

export default function NutritionModal({ item, imageRecipe, onClose }: { item: FoodItem; imageRecipe?: DiningItemImageRecipe; onClose: () => void }) {
  const servingDisplay = typeof item.serving_size === 'object' && item.serving_size !== null
    ? `${item.serving_size.serving_size_amount ?? ''} ${item.serving_size.serving_size_unit ?? ''}`.trim()
    : item.serving_size;
  const hasBreakdown = Object.values(item.nutrition).some((value) => value !== null);
  const hasLabels = item.dietary?.vegetarian || item.dietary?.vegan || item.is_spicy || item.dietary?.gluten_free;

  return <Modal isOpen onClose={onClose} title={item.name} size="md" ariaLabel={`Menu details for ${item.name}`}>
    <div className="space-y-5">
      {imageRecipe?.src ? <div className="flex flex-wrap items-start gap-4">
        <DiningItemPhoto key={imageRecipe.src} recipe={imageRecipe} large />
        <div className="min-w-0 flex-1 basis-[10rem] space-y-2 text-sm text-[var(--ss-text-muted)]">
          {imageRecipe.kind === 'illustrative' ? <p className="leading-6">An example of this type of food. The actual dish and portion may differ.</p> : null}
          {imageRecipe.sourceURL ? <a href={imageRecipe.sourceURL} target="_blank" rel="noopener noreferrer" className="inline-flex min-h-12 items-center text-[var(--ss-brand)] underline underline-offset-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ss-focus)]">Photo source</a> : null}
        </div>
      </div> : null}
      {item.description ? <p className="text-sm leading-6 text-[var(--ss-text-muted)]">{item.description}</p> : null}
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
        {item.calories !== null && item.has_nutrition_info ? <span className="font-semibold tabular-nums text-[var(--ss-text)]">{item.calories} calories</span> : null}
        <span className="text-[var(--ss-text-muted)]">Serving: {servingDisplay || 'not provided'}</span>
      </div>
      {hasLabels ? <div className="flex flex-wrap gap-2" aria-label="Dietary notes">
        {(item.dietary?.vegetarian || item.dietary?.vegan) ? <OperationalStatus tone="positive" icon={<Leaf className="h-3.5 w-3.5" />}>{item.dietary.vegan ? 'Vegan' : 'Vegetarian'}</OperationalStatus> : null}
        {item.is_spicy ? <OperationalStatus tone="critical" icon={<Flame className="h-3.5 w-3.5" />}>Spicy</OperationalStatus> : null}
        {item.dietary?.gluten_free ? <OperationalStatus tone="info">Gluten free</OperationalStatus> : null}
      </div> : <p className="text-sm text-[var(--ss-text-muted)]">Dietary labels not provided.</p>}
      <OperationalEvidence tone={item.allergens?.length ? 'critical' : 'neutral'}>
        <div className="mb-1 flex items-center gap-2 text-sm font-semibold"><AlertCircle className="h-4 w-4" aria-hidden="true" />Listed allergens</div>
        <p className="text-sm capitalize">{item.allergens?.length ? item.allergens.map((allergen) => allergen.replaceAll('_', ' ')).join(', ') : 'None listed'}</p>
        <p className="mt-2 text-xs leading-5">Confirm allergy needs with dining staff. Missing labels do not confirm allergy safety.</p>
      </OperationalEvidence>
      <section aria-label="Nutrition">
        <h3 className="mb-2 text-sm font-semibold text-[var(--ss-text)]">Nutrition</h3>
        {hasBreakdown ? <>
          <NutritionRow label="Protein" value={item.nutrition.protein_g} unit="g" />
          <NutritionRow label="Carbohydrates" value={item.nutrition.carbs_g} unit="g" />
          <NutritionRow label="Total fat" value={item.nutrition.fat_g} unit="g" />
          <NutritionRow label="Saturated fat" value={item.nutrition.saturated_fat_g} unit="g" />
          <NutritionRow label="Trans fat" value={item.nutrition.trans_fat_g} unit="g" />
          <NutritionRow label="Cholesterol" value={item.nutrition.cholesterol_mg} unit="mg" />
          <NutritionRow label="Fiber" value={item.nutrition.fiber_g} unit="g" />
          <NutritionRow label="Sugar" value={item.nutrition.sugar_g} unit="g" />
          <NutritionRow label="Sodium" value={item.nutrition.sodium_mg} unit="mg" />
          <NutritionRow label="Calcium" value={item.nutrition.calcium_mg} unit="mg" />
          <NutritionRow label="Iron" value={item.nutrition.iron_mg} unit="mg" />
        </> : <p className="text-sm text-[var(--ss-text-muted)]">Nutrition not provided.</p>}
      </section>
      <p className="text-xs text-[var(--ss-text-muted)]">Source: Rutgers Dining</p>
    </div>
  </Modal>;
}
